<template>
  <div class="case-review">
    <!-- 顶部工具栏：左侧状态筛选 + 右侧完成进度 -->
    <div class="case-review__toolbar">
      <!-- 状态筛选：status 为空表示全部，1/2/3 对应待评鉴/已保存/已评鉴 -->
      <van-dropdown-menu active-color="#EB1700" class="filter-menu case-review-filter-guide">
        <van-dropdown-item
          v-model="queryParams.status"
          :options="filterOptions"
          @change="handleFilterChange"
        />
      </van-dropdown-menu>
      <!-- 已完成数 / 总任务数 -->
      <div class="stats-badge case-review-stats-guide">
        已完成：<span class="stats-badge__num">{{ stats.submitted }}</span> / {{ stats.total }}
      </div>
    </div>

    <!-- 分页列表：滚动触底自动加载下一页 -->
    <van-list
      v-if="!isNone"
      v-model="loading"
      :finished="finished"
      finished-text="没有更多了"
      loading-text="正在加载..."
      @load="onLoad"
    >
      <!-- 双列卡片网格 -->
      <div class="case-review__content">
        <div
          class="task-item"
          v-for="item in list"
          :key="item.id"
          @click="handleItemClick(item)"
        >
          <!-- 封面 + 右上角状态角标 -->
          <div class="task-item__cover">
            <img :src="item.caseCoverUrl" alt="" />
            <span class="task-item__status" :class="`status-${item.status}`">
              <span>{{ getStatusText(item.status) }}</span>
              <span
                v-if="item.status === 3 && item.totalScore != null && item.totalScore !== ''"
                class="task-item__status-score"
              >({{ item.totalScore }}分)</span>
            </span>
          </div>
          <div class="task-item__body">
            <p class="task-item__title">{{ item.caseTitle || '-' }}</p>
            <p class="task-item__hospital">{{ item.caseHospital || '-' }}</p>
          </div>
        </div>
      </div>
    </van-list>

    <!-- 无数据空态 -->
    <div class="case-review__empty" v-if="isNone">
      <img src="@/assets/images/home/live_none.png" class="empty-img" alt="" />
      <div class="empty-text">暂无病例评鉴</div>
    </div>

    <!-- 批次全部评鉴完成提醒，每个批次仅提示一次 -->
    <van-dialog
      v-model="showCompletedDialog"
      :show-confirm-button="false"
      class="case-review-completed-dialog"
    >
      <div class="case-review-completed-dialog__body">
        <img class="case-review-completed-dialog__icon" src="@/assets/images/home/ic_success.svg" alt="">
        <div class="case-review-completed-dialog__title">评鉴提交成功</div>
        <div class="case-review-completed-dialog__text">感谢您的评鉴，评鉴内容已全部完成</div>
        <button type="button" class="case-review-completed-dialog__btn" @click="handleCompletedDialogConfirm">确认</button>
      </div>
    </van-dialog>
    <!--医鉴指引-->
    <GuideCaseReview ref="guideCaseReview" @finish="guideFinish" />
    <!--返回首页-->
    <returnBack backTxt="home" />
  </div>
</template>

<script>
import withRouteTracking from '@/utils/withRouteTracking'
import { getMyTasks, getMyTaskStats } from '@/api/caseReview'
import { getRemindBool, postRemind } from '@/api/simulator'
import { buildWapOpenidBindli } from '@/utils/util'
import GuideCaseReview from '@/components/GuideCaseReview.vue'
import returnBack from '@/views/person/components/returnBack.vue'

/** 任务状态文案映射，与接口 status 枚举一致 */
const STATUS_TEXT_MAP = {
  1: '待评鉴',
  2: '已保存',
  3: '已评鉴',
}

const CaseReviewPage = {
  name: 'caseReview',
  components: { GuideCaseReview, returnBack },
  data () {
    return {
      loading: false, // van-list 加载状态
      finished: false, // 是否已加载全部页
      isNone: false, // 是否展示空态
      loadLock: false, // 防止重复触发 onLoad
      list: [], // 任务列表（多页累加）
      // 列表查询参数
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        status: '', // 空字符串表示全部，不传 status
      },
      filterOptions: [
        { text: '全部', value: '' },
        { text: '待评鉴', value: 1 },
        { text: '已保存', value: 2 },
        { text: '已评鉴', value: 3 },
      ],
      // 顶部统计（与 PC 一致，统一为 number）
      stats: {
        total: 0,
        pending: 0,
        saved: 0,
        submitted: 0,
      },
      batchStats: [],
      showCompletedDialog: false,
      completedBatch: null,
    }
  },
  computed: {
    userInfo () {
      return this.$store.getters.userInfo
    },
  },
  mounted () {
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.refreshOnVisible()
      }
    }
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.fetchStats()
    this.startGuide()
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    /** 医鉴列表操作指引完成：记录已读 */
    async guideFinish () {
      if (!this.userInfo || !this.userInfo.id) return
      await postRemind({
        readType: 'MedicalExpertiseEvaluation',
        readTypeName: '医鉴操作指引',
        iepUserId: this.userInfo.id,
      })
    },
    /** 首次进入医鉴列表页展示操作指引 */
    async startGuide () {
      if (!this.userInfo || !this.userInfo.id) return
      const res = await getRemindBool({
        userId: this.userInfo.id,
        readType: 'MedicalExpertiseEvaluation',
      })
      if (res.code === 0 && !res.data) {
        await this.$nextTick()
        this.$refs.guideCaseReview.start([
          {
            step: '1',
            selector: '.case-review-filter-guide',
            icon: require('../../assets/images/caseReview/SliceQuanBu.png'),
            iconWidth: 260,
          },
          {
            step: '2',
            selector: '.case-review-stats-guide',
            icon: require('../../assets/images/caseReview/SliceYiWanCheng.png'),
            iconWidth: 220,
          },
        ])
      }
    },
    /** 归一化 stats 字段，便于比对与展示（接口可能返回 string） */
    toStats (data) {
      return {
        total: Number(data.total) || 0,
        pending: Number(data.pending) || 0,
        saved: Number(data.saved) || 0,
        submitted: Number(data.submitted) || 0,
      }
    },
    /** 写入统计并校验全部完成弹窗 */
    applyStats (data) {
      this.stats = this.toStats(data)
      this.batchStats = data.batches || []
      this.checkCaseReviewCompletedDialog(this.batchStats)
    },
    /** 页面重新可见时：仅比对统计，有变化才刷新列表 */
    async refreshOnVisible () {
      try {
        const { data } = await getMyTaskStats()
        if (!data) return
        const newStats = this.toStats(data)
        const unchanged = Object.keys(newStats).every(key => this.stats[key] === newStats[key])
        if (unchanged) return
        this.applyStats(data)
        this.handleFilterChange()
      } catch (error) {
        console.error('获取病例评鉴统计失败', error)
      }
    },
    /** 获取状态展示文案 */
    getStatusText (status) {
      return STATUS_TEXT_MAP[status] || '-'
    },
    /** 拉取医鉴任务统计（右上角已完成/总数） */
    async fetchStats () {
      try {
        const { data } = await getMyTaskStats()
        if (data) {
          this.applyStats(data)
        }
      } catch (error) {
        console.error('获取病例评鉴统计失败', error)
      }
    },
    /** 判断批次内任务是否全部已提交 */
    isBatchAllSubmitted (batch) {
      const total = Number(batch.total) || 0
      const submitted = Number(batch.submitted) || 0
      return total > 0 && submitted === total
    },
    /** 校验全部完成批次：stats.batches[].viewed 为 false 时弹窗 */
    checkCaseReviewCompletedDialog (batches) {
      if (!batches || !batches.length || this.showCompletedDialog) return

      for (const batch of batches) {
        if (this.isBatchAllSubmitted(batch) && batch.viewed === false) {
          this.completedBatch = batch
          this.showCompletedDialog = true
          return
        }
      }
    },
    /** 记录医鉴批次全部完成提醒已读（写入后同步更新本地 viewed） */
    async recordCaseReviewCompletedRead () {
      if (!this.userInfo || !this.userInfo.id || !this.completedBatch) return
      try {
        await postRemind({
          readType: `CaseReviewCompleted_${this.completedBatch.batchId}`,
          readTypeName: '医鉴批次全部完成已读',
          iepUserId: this.userInfo.id,
        })
        this.completedBatch.viewed = true
        const target = this.batchStats.find(item => item.batchId === this.completedBatch.batchId)
        if (target) target.viewed = true
      } catch (error) {
        console.error('记录医鉴全部完成已读失败:', error)
      }
    },
    /** 全部完成弹窗确认：记已读并关闭 */
    async handleCompletedDialogConfirm () {
      await this.recordCaseReviewCompletedRead()
      this.showCompletedDialog = false
      this.completedBatch = null
    },
    /** 重置分页与列表，用于切换筛选条件 */
    resetList () {
      this.list = []
      this.queryParams.pageNum = 1
      this.finished = false
      this.isNone = false
      this.loading = true
    },
    /** 筛选变更：清空当前列表并重新从第一页加载 */
    handleFilterChange () {
      this.resetList()
      this.onLoad()
    },
    /** 分页加载任务列表 */
    async onLoad () {
      if (this.loadLock || this.finished) return
      this.loadLock = true
      try {
        const { data } = await getMyTasks(this.queryParams)
        // 过滤已作废任务
        const pageList = ((data && data.list) || []).filter(item => item.voided !== 1)
        if (pageList.length < this.queryParams.pageSize) {
          this.finished = true
        }
        this.list = this.list.concat(pageList)
        this.isNone = this.list.length <= 0
        this.queryParams.pageNum++
      } catch (error) {
        this.finished = true
        if (this.list.length <= 0) {
          this.isNone = true
        }
        this.$toast(error.message || error.msg || '获取病例评鉴列表失败')
      } finally {
        this.loading = false
        this.loadLock = false
      }
    },
    /** 跳转外部病例详情页 */
    handleItemClick (item) {
      const trackParams = `&entrance=166&businessChannel=166`
      const openidBindli = buildWapOpenidBindli()
      window.location.href = `${process.env.bingliUrl}/wap/index.html#/casedetails?id=${item.caseId}&taskId=${item.id}${openidBindli}${trackParams}`
      // window.location.href = `https://192.168.1.197:8081/#/casedetails?id=${item.caseId}&taskId=${item.id}${openidBindli}${trackParams}`
      // window.location.href = `https://192.168.1.197:8081/#/homePage?channel=1${openidBindli}`
    },
  }
}

export default withRouteTracking(CaseReviewPage)
</script>

<style lang="less" scoped>
.case-review {
  min-height: 100%;
  background: #fff;
  padding: 0 10px 12px;
  box-sizing: border-box;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 8px;
    padding-bottom: 16px;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 120px);
    padding: 40px 0;

    .empty-img {
      width: 185px;
    }

    .empty-text {
      margin-top: 8px;
      font-size: 12px;
      color: #949ba5;
      line-height: 20px;
    }
  }
}

.filter-menu {
  padding: 0 10px;
  min-width: 0;

  /deep/ .van-dropdown-menu__bar {
    box-shadow: none;
    height: 44px;
    background: transparent;
  }

  /deep/ .van-dropdown-menu__title {
    font-size: 14px;
    color: #1a1a1a;
    padding-left: 0;
  }

  /deep/ .van-dropdown-menu__item {
    justify-content: flex-start;
  }
}

.stats-badge {
  flex-shrink: 0;
  padding: 4px 12px;
  background: #F9F8F7;
  border-radius: 14px;
  font-size: 14px;
  color: #666;
  line-height: 18px;
  white-space: nowrap;

  &__num {
    color: #eb1700;
    font-weight: 600;
  }
}

.task-item {
  width: calc((100% - 8px) / 2);
  box-sizing: border-box;

  &__cover {
    position: relative;
    width: 100%;
    height: 110px;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__status {
    position: absolute;
    top: 0;
    right: 0;
    padding: 3px 6px;
    border-radius: 0 6px 0 6px;
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    &-score {
      font-size: 10px;
      line-height: 14px;
    }

    /* 1-待评鉴 2-已保存 3-已评鉴 */
    &.status-1 {
      background: #8C3BBB;
    }

    &.status-2 {
      background: #EB1700;
    }

    &.status-3 {
      background: #328714;
    }
  }

  &__body {
    padding: 8px 2px 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a1a;
    line-height: 20px;
    margin-bottom: 4px;
    /* 最多两行，超出省略 */
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-all;
  }

  &__hospital {
    font-size: 12px;
    color: #949ba5;
    line-height: 18px;
    /* 单行超出省略 */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.case-review-completed-dialog {
  &__body {
    padding: 20px 18px 18px;
    text-align: center;
  }

  &__icon {
    display: block;
    width: 50px;
    height: 50px;
    margin: 0 auto 12px;
  }

  &__title {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  &__text {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
  }

  &__btn {
    width: 100%;
    height: 40px;
    margin-top: 20px;
    border: none;
    border-radius: 20px;
    background: #EB1700;
    color: #fff;
    font-size: 14px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/caseReview/index.vue